/** CUSTOMIZE AND MY TABS — one component, used by every left navigation.
 *
 *  The buttons and the dialog were living inside `components/layout/Sidebar.jsx`
 *  and were shown to everyone except a partner. They now live here so the
 *  Operational Base shell (`operationalBase/components/AdaptiveSidebar.jsx`)
 *  shows the SAME controls, writing the SAME saved setting
 *  (`authAPI.updateSidebarPrefs` → `user.sidebar_hidden_tabs`) — not a second
 *  copy of the feature.
 *
 *  Hiding is a personal display setting and nothing else: no link changes, no
 *  permission changes, nobody else affected. Customize and Reset to Default are
 *  always reachable, so nobody can be stuck with navigation they cannot get
 *  back, and the Command Center is never in the list to hide.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { authAPI } from '../../lib/api';
import { useAuth } from '../../lib/auth';

/** The Command Center can never be hidden, whichever shell is asking. */
export const NEVER_HIDDEN = ['command-center'];

/** Shared state for any shell that shows the controls. */
export const useSidebarPrefs = () => {
    const { user } = useAuth();
    const [hiddenTabs, setHiddenTabs] = useState(user?.sidebar_hidden_tabs || []);
    const [showAllTabs, setShowAllTabs] = useState(false);
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        setHiddenTabs(user?.sidebar_hidden_tabs || []);
    }, [user?.id, JSON.stringify(user?.sidebar_hidden_tabs || [])]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async (nextHidden) => {
        setSaving(true);
        try {
            await authAPI.updateSidebarPrefs(nextHidden);
            setHiddenTabs(nextHidden);
            if (user) user.sidebar_hidden_tabs = nextHidden;  // keep the auth snapshot in step
            if (nextHidden.length === 0) setShowAllTabs(false);
            toast.success(t('sidebarPrefs.saved'));
            return true;
        } catch {
            toast.error(t('sidebarPrefs.saveFailed'));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const isHidden = (id) => !showAllTabs && hiddenTabs.includes(id)
        && !NEVER_HIDDEN.includes(id);

    return { hiddenTabs, showAllTabs, setShowAllTabs, saving, save, isHidden };
};

/**
 *  items: [{ id, icon, labelKey | label, color }] — whatever THIS shell shows.
 *  prefs: what `useSidebarPrefs()` returned.
 */
export const SidebarPrefsControls = ({ items, prefs, tone = 'light' }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState([]);
    const { hiddenTabs, showAllTabs, setShowAllTabs, saving, save } = prefs;

    const list = useMemo(
        () => (items || []).filter((i) => i && i.id && !NEVER_HIDDEN.includes(i.id)),
        [items],
    );
    const label = (item) => (item.label || (item.labelKey ? t(item.labelKey) : item.id));
    const linkTone = tone === 'dark'
        ? 'text-white/55 hover:text-white/85'
        : 'text-slate-500 dark:text-slate-400';

    return (
        <>
            <div className="pt-3 mt-3 border-t border-slate-200 dark:border-slate-700 space-y-1">
                {hiddenTabs.length > 0 && (
                    <button
                        onClick={() => setShowAllTabs((v) => !v)}
                        className={`sidebar-link w-full ${linkTone}`}
                        data-testid="sidebar-show-all-btn"
                    >
                        <Eye size={18} />
                        <span className="text-sm">
                            {showAllTabs
                                ? t('sidebarPrefs.myTabs')
                                : `${t('sidebarPrefs.showAll')} (${hiddenTabs.length})`}
                        </span>
                    </button>
                )}
                {/* EVERY signed-in user, every vertical, every role. */}
                <button
                    onClick={() => { setDraft([...hiddenTabs]); setOpen(true); }}
                    className={`sidebar-link w-full ${linkTone}`}
                    data-testid="sidebar-customize-btn"
                >
                    <SlidersHorizontal size={18} />
                    <span className="text-sm">{t('sidebarPrefs.customize')}</span>
                </button>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="sidebar-customize-dialog">
                    <DialogHeader>
                        <DialogTitle>{t('sidebarPrefs.title')}</DialogTitle>
                        <DialogDescription>{t('sidebarPrefs.description')}</DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[50vh] overflow-y-auto space-y-1 pr-1">
                        <p className="text-xs text-slate-500 px-2 pb-1" data-testid="customize-pinned-note">
                            {t('sidebarPrefs.alwaysOn')}
                        </p>
                        {list.map((item) => {
                            const Icon = item.icon;
                            return (
                                <label
                                    key={item.id}
                                    className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700/60 cursor-pointer"
                                    data-testid={`customize-item-${item.id}`}
                                >
                                    <Checkbox
                                        checked={!draft.includes(item.id)}
                                        onCheckedChange={() => setDraft((prev) => (
                                            prev.includes(item.id)
                                                ? prev.filter((x) => x !== item.id)
                                                : [...prev, item.id]))}
                                        data-testid={`customize-checkbox-${item.id}`}
                                    />
                                    {Icon ? <Icon size={16} className={item.color || 'text-slate-500'} /> : null}
                                    <span className="text-sm">{label(item)}</span>
                                </label>
                            );
                        })}
                    </div>
                    <DialogFooter className="flex-col sm:flex-row sm:justify-between gap-2">
                        <Button variant="ghost" onClick={() => setDraft([])}
                                className="sm:mr-auto text-slate-500" data-testid="customize-reset">
                            <RotateCcw className="w-4 h-4 mr-1.5" />
                            {t('sidebarPrefs.reset')}
                        </Button>
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setOpen(false)} data-testid="customize-cancel">
                                {t('sidebarPrefs.cancel')}
                            </Button>
                            <Button onClick={async () => { if (await save(draft)) setOpen(false); }}
                                    disabled={saving} data-testid="customize-save">
                                {t('sidebarPrefs.save')}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default SidebarPrefsControls;
